'use client';

import { FC, ReactNode } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { PlacePrediction } from '@/hotdesk-ui/hooks/useSearchAutocomplete';
import styles from './SearchSuggestionsDropdown.module.scss';
export interface SearchSuggestionsDropdownProps {
  predictions: PlacePrediction[];
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (index: number) => void;
}

/** Bold the matched substring of the main text (what the user typed). */
function renderMainText(prediction: PlacePrediction): ReactNode {
  const {
    main_text,
    main_text_matched_substrings
  } = prediction.structured_formatting;
  const match = main_text_matched_substrings?.[0];
  if (!match) return main_text;
  const before = main_text.slice(0, match.offset);
  const matched = main_text.slice(match.offset, match.offset + match.length);
  const after = main_text.slice(match.offset + match.length);
  return <>
            {before}
            <span className={styles.match}>{matched}</span>
            {after}
        </>;
}
const SearchSuggestionsDropdown: FC<SearchSuggestionsDropdownProps> = ({
  predictions,
  activeIndex,
  onHover,
  onSelect
}) => {
  const t = useTranslations('explore');
  if (!predictions.length) return null;
  return <div className={styles.dropdown} role="listbox" data-sentry-component="SearchSuggestionsDropdown" data-sentry-source-file="SearchSuggestionsDropdown.tsx">
            <div className={styles.header}>{t('suggestions')}</div>
            <ul className={styles.list}>
                {predictions.map((prediction, index) => <li key={prediction.place_id} role="option" aria-selected={index === activeIndex} className={`${styles.item} ${index === activeIndex ? styles.itemActive : ''}`} onMouseEnter={() => onHover(index)} onMouseDown={e => {
        // Keep input focus; select before the input's blur.
        e.preventDefault();
        onSelect(index);
      }}>
                        <span className={styles.icon}>
                            <MapPin size={19} strokeWidth={1.75} />
                        </span>
                        <span className={styles.text}>
                            <span className={styles.mainText}>
                                {renderMainText(prediction)}
                            </span>
                            {prediction.structured_formatting.secondary_text && <span className={styles.subtitle}>
                                    {prediction.structured_formatting.secondary_text}
                                </span>}
                        </span>
                    </li>)}
            </ul>
            <div className={styles.footer}>{t('searchHints')}</div>
        </div>;
};
export default SearchSuggestionsDropdown;